/**
 * PublicNavAccountMenu
 *
 * Account dropdown shown in the public/marketing header when the visitor is
 * already signed in. Replaces Sign in / Start for Free so logged-in users are
 * not prompted to create another account.
 *
 * Business rules:
 * - Primary action is "Go to Overview" (authenticated home), styled like the
 *   in-app portal switch so it is easy to find.
 * - Admins also get Admin Portal in the same menu.
 * - My Account and Logout match the main AppShell user menu.
 */

import { useNavigate } from 'react-router-dom';
import { Avatar, Group, Menu, Text, UnstyledButton } from '@mantine/core';
import {
  IconHome,
  IconLogout,
  IconShield,
  IconUser,
} from '@tabler/icons-react';
import { brandColors } from '../../config/theme';
import { landingContent } from '../../content/landingContent';
import { APP_ROUTES } from '../../constants/routes';
import { useAuth } from '../../contexts/AuthContext';
import portalSwitchMenuItemClasses from '../common/PortalSwitchMenuItem.module.css';

/**
 * Renders the signed-in account avatar + dropdown for the public nav.
 */
export const PublicNavAccountMenu = () => {
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();

  const displayName =
    user?.name
    || `${user?.givenName || ''} ${user?.surname || ''}`.trim()
    || user?.email;

  return (
    <Menu shadow="md" width={220} position="bottom-end" withinPortal>
      <Menu.Target>
        <UnstyledButton
          aria-label="Account menu"
          data-testid="public-nav-account-menu"
        >
          <Group gap="xs" wrap="nowrap">
            <Avatar
              src={user?.profilePictureUrl}
              name={displayName}
              color="initials"
              size="sm"
              radius="xl"
            />
            <Text size="sm" visibleFrom="sm" lineClamp={1}>
              {displayName}
            </Text>
          </Group>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item
          leftSection={<IconHome size={14} />}
          onClick={() => navigate(APP_ROUTES.overview)}
          className={portalSwitchMenuItemClasses.portalSwitchItem}
          data-testid="public-nav-go-to-overview"
        >
          {landingContent.cta.goToApp}
        </Menu.Item>

        {isAdmin && (
          <Menu.Item
            leftSection={<IconShield size={14} />}
            onClick={() => navigate(APP_ROUTES.adminRoot)}
            data-testid="public-nav-admin-portal"
          >
            Admin Portal
          </Menu.Item>
        )}

        <Menu.Item
          leftSection={<IconUser size={14} />}
          onClick={() => navigate(APP_ROUTES.account)}
          data-testid="public-nav-my-account"
        >
          My Account
        </Menu.Item>

        <Menu.Divider />

        <Menu.Item
          leftSection={<IconLogout size={14} />}
          color={brandColors.danger}
          onClick={logout}
          data-testid="public-nav-logout"
        >
          Logout
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};
